import React from 'react'
import { Dropdown, Radio, NumberInputDropdown, WarningText } from 'components/UIElements'
import { fromUTCMidnightSeconds, currentDayStartInUTC, DAYS_IN_MONTH } from 'utils/time'
import { pluralize } from 'utils/misc'
import _dayOptions, {
  REPEAT_TIME_UNIT_OPTION,
  UNIFICATION_REPEAT_TIME_UNIT_OPTION,
  RECURRING_AFTER_TIME_OPTION_DEFAULT,
  isRepeatTimeUnitOpt,
  isUnificationRepeatTimeUnitOpt,
  isHours,
  monthOptions,
} from '../constants/schedulerConstants'
import { DEPLOYMENT_KEYS } from 'utils/constants'
import contentConnect from '../containers/ContentContainer'

class RecurrenceContent extends React.Component {
  static NEVER_KEY = DEPLOYMENT_KEYS.never
  static CHAIN_EXPIRE_OFFSET_KEY = DEPLOYMENT_KEYS.chainExpireOffset
  getRecurrence = (duration, interval) => Math.floor(duration / interval) + (duration % interval !== 0 ? 1 : 0)

  componentDidUpdate(prevProps) {
    // When "Never expire + Unless visit confirmation applies" is selected, UI shows "Repeat after 1 day"
    // but recurring can still be null, so the dropdown shows blank and interaction creates a partial
    // object (no scheme) and deselects all radios. Initialize recurring to n-days default once.
    const { recurring, updateRecurring, expire, isDeployedByVisit } = this.props
    const hasVisitConfirmationNeverExpire =
      isDeployedByVisit && !!expire?.expire_on_visit_confirmation
    const alreadyInitialized = this._visitConfirmationRecurringInitialized
    if (
      hasVisitConfirmationNeverExpire &&
      !recurring?.scheme &&
      !alreadyInitialized
    ) {
      this._visitConfirmationRecurringInitialized = true
      const _default = { ...RECURRING_AFTER_TIME_OPTION_DEFAULT['n-days'] }
      delete _default.recurrences
      updateRecurring(_default)
    }
    if (!hasVisitConfirmationNeverExpire) this._visitConfirmationRecurringInitialized = false
  }

  calculateNumRecurrences = interval => {
    const { deploy, expire } = this.props
    if (interval === '') return
    let instrumentDuration = 1
    if (expire.absolute) {
      const endDate = fromUTCMidnightSeconds(expire.absolute)
      const startDate = 'now' in deploy ? currentDayStartInUTC() : fromUTCMidnightSeconds(deploy.absolute)
      instrumentDuration = endDate.diff(startDate, 'days')
    }
    if (expire.first_login) instrumentDuration = expire.first_login.interval - deploy.first_login.interval
    if (expire.chain_expire_offset) instrumentDuration = expire.chain_expire_offset - deploy.chain_deploy_offset
    if (expire.relative_to_send_ideal) instrumentDuration = expire.relative_to_send_ideal.interval - 1
    return this.getRecurrence(instrumentDuration, interval)
  }

  onUpdateRecurringField = ({ num, isNDays, isNMonths }) => {
    let _num = num
    // When repeat is selected, avoid writing 0 or empty interval so schema doesn't reset (PMT-1537)
    if (_num === 0 && (isNDays || isNMonths)) _num = 1
    else if (_num === 0) _num = ''

    const { expire, updateRecurringField, updateRecurring, recurring, isDeployedByVisit } = this.props
    const hasNeverExpire = !!expire.hasOwnProperty(RecurrenceContent.NEVER_KEY)
    const hasVisitConfirmationNeverExpire =
      !!expire?.expire_on_visit_confirmation && isDeployedByVisit && hasNeverExpire

    // When "Never expire + Unless visit confirmation applies" is selected, recurring can still be
    // null while the UI shows "Repeat after every". Using updateRecurringField then creates
    // { interval, visible_days } with no scheme, so radios appear deselected. Use full object.
    if (hasVisitConfirmationNeverExpire && (!recurring || !recurring.scheme) && (isNDays || isNMonths)) {
      const scheme = isNMonths ? 'n-months' : 'n-days'
      const _default = { ...RECURRING_AFTER_TIME_OPTION_DEFAULT[scheme] }
      _default.interval = _num
      _default.visible_days = isNMonths ? DAYS_IN_MONTH * _num : _num
      if (hasNeverExpire) delete _default.recurrences
      updateRecurring({ ..._default })
      return
    }

    updateRecurringField('interval', _num, 'schedule.recurring.interval')
    if (isNDays) {
      updateRecurringField('visible_days', _num, 'schedule.recurring.visible_days')
    }
    if (isNMonths) {
      updateRecurringField('visible_days', DAYS_IN_MONTH * _num, 'schedule.recurring.visible_days')
    }
    if (!hasNeverExpire) {
      updateRecurringField('recurrences', this.calculateNumRecurrences(_num), 'schedule.recurring.recurrences')
    }
  }

  getStateFromProps = () => {
    const { recurring, deploy, expire, isDeployedByVisit } = this.props

    // When "Expire after X occurrences" is selected, we already disable the
    // "Doesn't Repeat" and "Repeat Immediately" options via `hasOccurrences`.
    //
    // For visit-triggered instruments, when "Never expire" is selected together
    // with "Unless visit confirmation applies", the repeat behaviour should be
    // locked in the same way – i.e. the admin should not be able to switch back
    // to "Doesn't Repeat" or "Repeat Immediately" from the repeat menu.
    // For visit-triggered instruments, the UX requires that once
    // "Never expire + Unless visit confirmation applies" is chosen,
    // the repeat behaviour is effectively locked. In that case we can
    // safely treat `hasOccurrences` as always true regardless of the
    // current `recurring` object.
    if (isDeployedByVisit && !!expire?.expire_on_visit_confirmation) {
      return { hasOccurrences: true, disableInterval: false }
    }

    // For non-visit instruments, treat the presence of both `never` and any
    // recurring config as an "expire after occurrences" state. We don't need
    // to rely specifically on the `recurrences` field here – once a recurring
    // schedule exists alongside `never`, the other options should remain
    // disabled until the admin explicitly clears the recurring config.
    const hasOccurrences = !!recurring && !!expire[RecurrenceContent.NEVER_KEY]
    let disableInterval
    if (hasOccurrences) {
      disableInterval = false
    } else if (
      !!expire.hasOwnProperty(RecurrenceContent.NEVER_KEY) ||
      expire.hasOwnProperty(RecurrenceContent.CHAIN_EXPIRE_OFFSET_KEY)
    ) {
      disableInterval = false
    } else if (expire.hasOwnProperty('first_login')) {
      disableInterval = expire.first_login.interval - deploy.first_login.interval < 2
    } else if (expire.hasOwnProperty('relative_to_send_ideal')) {
      disableInterval = expire.relative_to_send_ideal.interval < 2
    } else {
      disableInterval = fromUTCMidnightSeconds(expire.absolute).isSame(fromUTCMidnightSeconds(deploy.absolute), 'day')
    }
    return { hasOccurrences, disableInterval }
  }

  onTimeDropdownSelect = item => {
    this._updateRecurring(item.key)
  }

  /**
   * Update the time unit for recurring
   * @param {String} timeUnitKey
   */
  _updateRecurring = timeUnitKey => {
    const { recurring, updateRecurring, expire, isDeployedByVisit } = this.props
    const _default = { ...RECURRING_AFTER_TIME_OPTION_DEFAULT[timeUnitKey] }

    // Check if we're in the "Never expire + Unless visit confirmation applies" state
    const isNeverExpireWithVisitConfirmation =
      !!expire.hasOwnProperty(RecurrenceContent.NEVER_KEY) &&
      isDeployedByVisit &&
      !!expire.expire_on_visit_confirmation

    const hasNeverExpire = !!expire.hasOwnProperty(RecurrenceContent.NEVER_KEY)
    if (!hasNeverExpire) {
      const recurrences = this.calculateNumRecurrences(1)
      if (recurrences !== undefined) _default.recurrences = recurrences
    } else if (isNeverExpireWithVisitConfirmation) {
      // In the "Never expire + Unless visit confirmation applies" case,
      // we must NOT set recurrences in the recurring object, otherwise
      // it will toggle from "Never expire" to "Expire after x occurrences"
      // Remove recurrences from _default if it exists
      delete _default.recurrences
    }

    const newRecurring = { ...recurring, ..._default }

    // If we're in "Never expire + Unless visit confirmation applies" state,
    // ensure recurrences is not in the final object
    if (isNeverExpireWithVisitConfirmation) {
      delete newRecurring.recurrences
    }

    if (this.isTime(timeUnitKey)) {
      delete newRecurring.visible_days
    }
    updateRecurring(newRecurring)
  }

  isTime = recurringScheme => {
    const { hasInstrumentUnification, monthlyRepeatEnabled } = this.props

    const options = hasInstrumentUnification ? UNIFICATION_REPEAT_TIME_UNIT_OPTION : REPEAT_TIME_UNIT_OPTION
    const filteredOptions = options.filter(option => {
      if (option.key === 'n-months' && !monthlyRepeatEnabled) {
        return false
      }
      return true
    })

    return filteredOptions
      .filter(opt => !opt.key.includes('n-days') && !opt.key.includes('n-months'))
      .map(opt => opt.key)
      .includes(recurringScheme)
  }

  render() {
    const {
      recurring,
      expire,
      isDeployedByVisit,
      updateExpire,
      updateRecurring,
      errors,
      hideImmediateRepeat,
      hasInstrumentUnification,
      isReadOnlyState,
      monthlyRepeatEnabled,
    } = this.props

    const repeatTime = hasInstrumentUnification
      ? isUnificationRepeatTimeUnitOpt(recurring?.scheme)
      : isRepeatTimeUnitOpt(recurring?.scheme)

    const hasVisitConfirmationNeverExpire = isDeployedByVisit && !!expire?.expire_on_visit_confirmation

    // Use fallback 1 when interval is missing so days drop-down isn't empty and doesn't reset (PMT-1537)
    const intervalDisplayValue =
      recurring && repeatTime
        ? (recurring.interval != null && recurring.interval >= 1 ? recurring.interval : 1)
        : hasVisitConfirmationNeverExpire
          ? 1
          : ''

    const hasExpireOnVisitConfirmation = !!expire?.expire_on_visit_confirmation

    const selected = {
      // In the visit-triggered + "Never expire + Unless visit confirmation applies" case,
      // we never want "Doesn't repeat" to be the active choice.
      no_repeat: !recurring && !hasVisitConfirmationNeverExpire,
      // For that same case, "Repeat after every" should always appear selected,
      // regardless of the internal `repeatTime` calculation.
      repeat_after: hasVisitConfirmationNeverExpire || (recurring && repeatTime),
      // Likewise, "Repeat Immediately" should never be the active choice in that flow.
      immediately: !hasExpireOnVisitConfirmation && recurring && recurring.scheme === 'persistent',
    }

    // TODO: Set this to false before BE has this support as well such as UI has been confirmed.
    const newNHoursSupportEnabled = true

    const options = hasInstrumentUnification ? UNIFICATION_REPEAT_TIME_UNIT_OPTION : REPEAT_TIME_UNIT_OPTION
    const filteredOptions = options.filter(option => {
      if (option.key === 'n-months' && !monthlyRepeatEnabled) {
        return false
      }
      return true
    })

    const isNMonthsScheme = recurring?.scheme === 'n-months'
    const isNDaysScheme = recurring?.scheme === 'n-days'

    const { hasOccurrences, disableInterval } = this.getStateFromProps()
    return (
      <div className='repeat-menu'>
        <p>Should this instrument repeat?</p>
        <Radio
          disabled={hasOccurrences || isReadOnlyState}
          selected={selected.no_repeat}
          onClick={() => {
            if (recurring && recurring.recurrences) {
              updateExpire({ never: null })
            }
            updateRecurring(null)
          }}
          id='does-not-repeat'
          content="Doesn't Repeat"
        />
        <Radio
          id='repeat-after-every'
          disabled={disableInterval || isReadOnlyState}
          selected={selected.repeat_after}
          onClick={() => {
            this._updateRecurring(filteredOptions[0].key)
          }}
          content={
            <div className='flexed'>
              Repeat after every
              {this.isTime(recurring?.scheme) ? (
                <input
                  className='occurence-input'
                  aria-label='repeat-hours-input'
                  min={recurring?.scheme ? 1 : 10}
                  max={isHours(recurring?.scheme) ? 24 : 60}
                  type='number'
                  id='repeat-hours-input'
                  value={selected.repeat_after ? intervalDisplayValue : ''}
                  onChange={e => {
                    this.onUpdateRecurringField({ num: Math.floor(Number(e.target.value)) || 1 })
                  }}
                  disabled={isReadOnlyState}
                />
              ) : isNMonthsScheme ? (
                <NumberInputDropdown
                  id='repeat-month-input'
                  value={selected.repeat_after ? intervalDisplayValue : ''}
                  options={monthOptions}
                  onChange={num => this.onUpdateRecurringField({ num, isNMonths: true })}
                  disabled={isReadOnlyState}
                />
              ) : (
                <NumberInputDropdown
                  id='repeat-day-input'
                  value={selected.repeat_after ? intervalDisplayValue : ''}
                  options={_dayOptions}
                  onChange={num => this.onUpdateRecurringField({ num, isNDays: true })}
                  disabled={isReadOnlyState}
                />
              )}
              {newNHoursSupportEnabled ? (
                <Dropdown
                  id='time-unit-options-dropdown'
                  selected={recurring?.scheme || filteredOptions[0].key}
                  onSelect={this.onTimeDropdownSelect}
                  options={filteredOptions.map(opt => {
                    const optText = opt.text
                    const text =
                      recurring && selected.repeat_after
                        ? pluralize(recurring.interval, optText.substring(0, optText.length - 1), optText, false)
                        : optText
                    return {
                      ...opt,
                      text,
                    }
                  })}
                  disabled={isReadOnlyState}
                />
              ) : recurring && selected.repeat_after ? (
                pluralize(recurring.interval, 'day', 'days', false)
              ) : (
                'days'
              )}
              <WarningText text={errors['schedule.recurring.interval']} />
            </div>
          }
        />
        {!hideImmediateRepeat && (
          <Radio
            disabled={hasOccurrences || isReadOnlyState}
            selected={selected.immediately}
            onClick={() => {
              updateRecurring({ scheme: 'persistent' })
            }}
            id='repeat-immediately'
            content='Repeat Immediately'
          />
        )}
      </div>
    )
  }
}

export default contentConnect(RecurrenceContent)
